import React from 'react';
import { useUserRoles } from '@/hooks/use-user-roles';

interface LinkifyContentProps {
  content: string;
  className?: string;
  authorId?: string;
}

/**
 * Component that renders text content with clickable hyperlinks only for admin, premium, and partner users.
 * Regular users see plain text without hyperlinks.
 */
export const LinkifyContent: React.FC<LinkifyContentProps> = ({ content, className, authorId }) => {
  const { isAdmin, isPremium, isPartner } = useUserRoles(authorId);
  
  // Check if user has permission for clickable links
  const canHaveLinks = isAdmin || isPremium || isPartner;
  
  if (!canHaveLinks) {
    // Regular users: just show plain text
    return <span className={className}>{content}</span>;
  }
  
  // Privileged users: convert URLs to clickable links
  // URL regex pattern - matches http(s) URLs
  const urlPattern = /(https?:\/\/[^\s]+)/g;
  
  const parts = content.split(urlPattern);
  
  return (
    <span className={className}>
      {parts.map((part, index) => {
        // Check if this part is a URL
        if (urlPattern.test(part)) {
          return (
            <a
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-400 hover:text-purple-300 underline transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </span>
  );
};
